'use client';

import { FC, useMemo, useState } from 'react';
import { Locale } from '@bts-web/utils-lokalise';
import { useFeatureFlag } from '../../../statsig/sdk/provider';
import {
  extractPrecisionFloatValueWithDecimalsForNonUiComparisons,
  PrecisionFloat,
} from '@bts-web/utils-formatting';
import { TradeControllerVariants, TradeScreenTranslations } from '../../utils';
import { TabBar } from '../../../common/components/TabBar/TabBar';
import { getTranslationsPerTradeType } from '../MainTradeController/utils/getTranslationsPerTradeType';
import { MainTradeDataFlowWrapper } from '../MainTradeDataFlowWrapper/MainTradeDataFlowWrapper';
import { MutableNonNullableAsset } from '../types';
import { removeInputValueFromLocalStorage } from '../utils/localStorageInputValueUtils';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { TradeOptionsDrawer } from '../../TradeDrawer/TradeDrawerProper/TradeOptionsDrawer';
import { useClientUserInfo } from '../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';

interface AssetPageInitiateTradeBoxProps {
  assetInitialData: MutableNonNullableAsset;
  targetAssetInitialData?: MutableNonNullableAsset;
  currentLocale: Locale;
  currency: string;
  tradeTranslations: TradeScreenTranslations;
  initialExchangeRate?: PrecisionFloat;
}

export const AssetPageInitiateTradeBox: FC<AssetPageInitiateTradeBoxProps> = ({
  assetInitialData,
  currency,
  currentLocale,
  tradeTranslations,
  targetAssetInitialData,
  initialExchangeRate,
}) => {
  const feature_hasSavingsPlansEnabled = useFeatureFlag(
    'feature_hasSavingsPlansEnabled',
  );

  const feature_hasStakingEnabled = useFeatureFlag('feature_hasStakingEnabled');

  const feature_hasSwapTradeEnabled = useFeatureFlag(
    'feature_hasSwapTradeEnabled',
  );

  const { isIslamicUser } = useClientUserInfo();

  // For removing trade form previous values in case trade failed.
  removeInputValueFromLocalStorage();

  const [transactionVariant, setTransactionVariant] =
    useState<TradeControllerVariants>('buy');

  const { translatedInnerValues } = getTranslationsPerTradeType({
    assetType: assetInitialData.__typename,
    translations: tradeTranslations,
    variant: transactionVariant,
  });

  const { openDrawer } = useModalDrawerContext();

  const handleOptionsOnClick = () => {
    const isOwningAsset =
      Number(assetInitialData?.portfolio?.assetBalance?.value || '0') > 0;

    openDrawer(TradeOptionsDrawer, { isOwningAsset });
  };

  const canSell = useMemo(() => {
    const fiatBalance =
      extractPrecisionFloatValueWithDecimalsForNonUiComparisons(
        assetInitialData?.portfolio?.fiatBalance,
      );

    const minimumSellAmount =
      extractPrecisionFloatValueWithDecimalsForNonUiComparisons(
        assetInitialData?.minimumSellAmount,
      );

    const isAssetOwned = fiatBalance > 0;

    return isAssetOwned && minimumSellAmount <= fiatBalance;
  }, [
    assetInitialData.minimumSellAmount,
    assetInitialData.portfolio?.fiatBalance,
  ]);

  const tabItems = [
    {
      label: tradeTranslations.buy,
      value: 'buy',
    },
    {
      label: tradeTranslations.sell,
      value: 'sell',
      disabled: !canSell,
    },
    ...(feature_hasSwapTradeEnabled && !isIslamicUser
      ? [
          {
            label: tradeTranslations.swap,
            value: 'swap',
            disabled: !canSell,
          },
        ]
      : []),
    ...(feature_hasSavingsPlansEnabled
      ? [
          {
            label: tradeTranslations.savingsPlan,
            value: 'savings',
          },
        ]
      : []),
    ...(feature_hasStakingEnabled && !isIslamicUser
      ? [
          {
            label: tradeTranslations.stake,
            value: 'stake',
          },
        ]
      : []),
  ];

  return (
    <>
      <TabBar
        onValueChange={(value) => {
          if (value === 'options') {
            handleOptionsOnClick();
          } else {
            setTransactionVariant(value as TradeControllerVariants);
          }
        }}
        tabItems={tabItems}
        value={transactionVariant}
      />

      <MainTradeDataFlowWrapper
        key={`trade-${transactionVariant}`} // ensures that the inner values are reset when the variant changes
        initialData={{
          assetData: assetInitialData,
          initialSwapExchangeRate: initialExchangeRate,
          targetAssetData: targetAssetInitialData,
        }}
        staticData={{
          currency,
          currentLocale,
          translatedInnerValues,
          translations: tradeTranslations,
          variant: transactionVariant,
        }}
        overrideOperationWithVariant={transactionVariant}
        openBuyTab={() => setTransactionVariant('buy')}
      />
    </>
  );
};
